import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { datadogRum } from '@datadog/browser-rum';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datetime/css/react-datetime.css';

import 'Configuration/AgGridConfiguration';
import { getMsalIdToken } from 'Services/MsalTokenService';
import {
  getSeasons,
  getCountries,
  getDestinations,
  getJobTitles,
  getKeyWords,
} from 'Store/Actions/MasterData/MasterDataAction';
import { getUser } from 'Store/Actions/User/UserAction';
import { getEnvironmentVariablesHandler } from 'Handlers/EnvHandler';
import { DarkModeProvider } from 'Configuration/DarkModeContext';
import Layout from 'Layout/Layout';
import TppSwitcher from 'Layout/Components/F2wSwitcher';
import F2WCustomLogger from 'Services/F2WCustomLogger';
import { getDatadogClientToken } from 'Constants/DatadogConstants';

const initializeApp = async (dispatch) => {
  await Promise.all([
    dispatch(getSeasons()),
    dispatch(getCountries()),
    dispatch(getDestinations()),
    dispatch(getJobTitles()),
    dispatch(getKeyWords()),
    dispatch(getUser()),
  ]);
};

const sanitizeVersion = (version) => {
  if (!version) return 'unknown';

  let sanitized = version.toString().trim();

  if (!/^[a-zA-Z]/.test(sanitized)) {
    sanitized = 'v' + sanitized;
  }

  sanitized = sanitized.replace(/[^a-zA-Z0-9_\-:./]/g, '_');

  return sanitized.toLowerCase();
};

const APP = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        await initializeApp(dispatch);
        setIsInitialized(true);
      } catch (error) {
        F2WCustomLogger.error('Initialization failed', error, { component: 'App' });
      } finally {
        setIsLoading(false);
      }
    };

    const initDatadog = async () => {
      if (datadogRum.getInternalContext()) return;

      try {
        const envVars = getEnvironmentVariablesHandler();
        const sanitizedVersion = sanitizeVersion(envVars.version);

        datadogRum.init({
          applicationId: 'a6f03881-8aec-4ac4-bbec-80f6c7771b46',
          clientToken: getDatadogClientToken(),
          site: 'datadoghq.eu',
          service: 'fly2work-web',
          env: envVars.environment,
          version: sanitizedVersion,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'allow',
        });

        const token = await getMsalIdToken();
        const profile = jwtDecode(token);
        datadogRum.setUser({
          email: profile.upn || profile.email,
          name: profile.name || profile.given_name,
        });
      } catch (error) {
        F2WCustomLogger.error('Failed to initialize Datadog', error, { component: 'App' });
      }
    };

    initDatadog();
    initialize();
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-2x me-2" />
          <span className="fs-5">Loading Fly2Work Web...</span>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <h3>Failed to initialize application. Please refresh the page.</h3>
      </div>
    );
  }

  return (
    <DarkModeProvider>
      <Layout>
        <TppSwitcher />
      </Layout>
    </DarkModeProvider>
  );
};

export default APP;
